import { useState, type CSSProperties, type RefObject } from 'react';
import {
  BlueprintIcon,
  FolderIcon,
  GitDiffIcon,
  GlobeSimpleIcon,
  MonitorIcon,
  NetworkIcon,
  TerminalWindowIcon,
} from '@phosphor-icons/react';

import { useSessionPlan } from '@/api-models/session-plans';
import { TabBar } from '@/ui/Tabs';
import classNames from '@/utils/classnames';

import { MIN_THREAD_WIDTH, type PanelMode } from './panel-layout';
import { AssistantMarkdown } from './thread/AssistantMarkdown';
import { DesktopTab } from './tabs/DesktopTab';
import { FilesTab } from './tabs/FilesTab';
import { PortsTab } from './tabs/PortsTab';
import { PreviewTab } from './tabs/PreviewTab';
import { ReviewTab } from './tabs/ReviewTab';
import { TerminalTab } from './tabs/TerminalTab';

type SidePanelTab = 'plan' | 'files' | 'review' | 'terminal' | 'preview' | 'ports' | 'desktop';

const PLAN_TAB = { value: 'plan' as const, label: 'Plan', icon: <BlueprintIcon size={14} /> };

const TABS = [
  { value: 'files' as const, label: 'Files', icon: <FolderIcon size={14} /> },
  { value: 'review' as const, label: 'Review', icon: <GitDiffIcon size={14} /> },
  { value: 'terminal' as const, label: 'Terminal', icon: <TerminalWindowIcon size={14} /> },
  { value: 'preview' as const, label: 'Preview', icon: <GlobeSimpleIcon size={14} /> },
  { value: 'ports' as const, label: 'Ports', icon: <NetworkIcon size={14} /> },
  { value: 'desktop' as const, label: 'Desktop', icon: <MonitorIcon size={14} /> },
];

/**
 * Files / Terminal / Ports / Desktop for a session's sandbox. All tabs are keyed
 * by the session's sandbox proxy base so they hit `/api/v1/agent-sessions/:id/*`.
 *
 * The chrome stays mounted in every state but the bodies only render once `ready`.
 * That's a correctness requirement, not just anti-layout-shift: every one of
 * these routes resolves through `ensure_session_sandbox_live`, so mounting a tab
 * against a paused sandbox silently restarts it — defeating the whole point of
 * showing the session as idle and waiting for the user to ask for it back.
 *
 * Unmounting `DesktopTab` is safe despite that: its VNC connection lives in a
 * keep-alive surface outside React (see `desktop-surface.ts`), so remounting
 * shows the still-streaming desktop rather than reconnecting to it.
 *
 * `hidden` is `display: none` for the same reason — collapsing the panel must not
 * kill the PTY behind the terminal tab. Both the terminal and the desktop surface
 * already treat a 0×0 box as "not visible" and go quiet on their own.
 */
interface SidePanelProps {
  sessionId: string;
  ready: boolean;
  mode: PanelMode;
  /** Width in px, honoured in `split` mode only. */
  width: number;
  panelRef: RefObject<HTMLDivElement | null>;
}

export function SidePanel({ sessionId, ready, mode, width, panelRef }: SidePanelProps) {
  const [tab, setTab] = useState<SidePanelTab>('files');
  const base = `/api/v1/agent-sessions/${sessionId}`;
  // Only sessions that planned first have one. Unlike every other tab this needs no sandbox, so
  // it renders whether or not the box is up — which is the point: the plan is what the agent is
  // working from, and it should be readable while you wait for the sandbox to come online.
  const { plan } = useSessionPlan(sessionId);
  const tabs = plan ? [PLAN_TAB, ...TABS] : TABS;

  // The max-width isn't decoration: the panel is `shrink-0`, so without it a
  // window narrow enough to undercut a previously-dragged width would squeeze
  // the (`min-w-0`) thread down to nothing. Flex honours max-width when it
  // resolves the hypothetical main size, so CSS does the clamping for us and no
  // window-resize listener is needed.
  const style: CSSProperties =
    mode === 'hidden'
      ? { display: 'none' }
      : mode === 'full'
        ? {}
        : { width, maxWidth: `calc(100% - ${MIN_THREAD_WIDTH}px)` };

  return (
    <div
      ref={panelRef}
      style={style}
      className={classNames('flex flex-col bg-surface-2', mode === 'full' ? 'min-w-0 flex-1' : 'shrink-0')}
    >
      <div className="flex h-11 shrink-0 items-center border-b border-line-subtle px-2">
        <TabBar value={tab} onChange={setTab} tabs={tabs} ariaLabel="Session panel" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {tab === 'plan' && plan ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <AssistantMarkdown content={plan.content_md} variant="document" />
          </div>
        ) : !ready ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center text-xs text-fg-tertiary">
            Files, terminal, ports and desktop appear once the sandbox is online.
          </div>
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col" style={{ display: tab === 'files' ? 'flex' : 'none' }}>
              <FilesTab base={base} />
            </div>
            <div className="flex min-h-0 flex-1 flex-col" style={{ display: tab === 'review' ? 'flex' : 'none' }}>
              {tab === 'review' && <ReviewTab base={base} sessionId={sessionId} />}
            </div>
            <div className="flex min-h-0 flex-1 flex-col" style={{ display: tab === 'terminal' ? 'flex' : 'none' }}>
              <TerminalTab base={base} />
            </div>
            <div className="flex min-h-0 flex-1 flex-col" style={{ display: tab === 'preview' ? 'flex' : 'none' }}>
              {tab === 'preview' && <PreviewTab base={base} />}
            </div>
            <div className="flex min-h-0 flex-1 flex-col" style={{ display: tab === 'ports' ? 'flex' : 'none' }}>
              {tab === 'ports' && <PortsTab base={base} />}
            </div>
            <div className="flex min-h-0 flex-1 flex-col" style={{ display: tab === 'desktop' ? 'flex' : 'none' }}>
              {tab === 'desktop' && <DesktopTab base={base} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
